import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { api } from 'api';
import { Modal, ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';

type Props = ModalProps<{
  applicationName: string;
  applicationId: number;
  redirectUrl?: string;
}>;

export const RemoveApplicationModal = (props: Props) => {
  const { applicationId, applicationName, redirectUrl, closeModal } = props;
  const { applicationStore, t } = useStore();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToaster();
  const queryClient = useQueryClient();
  const submit = useMutation(() => api.applications.remove(applicationId), {
    onSuccess: () => {
      showSuccess(t('application.store.delete.success', applicationName));
      setTimeout(() => queryClient.invalidateQueries(['applicants']), 2000);
      queryClient.invalidateQueries(['favorites']);
      applicationStore.delete(applicationId);
      if (redirectUrl) {
        navigate(redirectUrl);
      }
      closeModal();
    },
    onError: () => {
      showError(t('application.store.delete.error', applicationName));
    },
  });
  return (
    <Modal>
      <Modal.Header text={t('application.removeconfirm.header')} />
      <Stack space={4}>
        <Text text={t('application.removeconfirm.confirm', applicationName)} />
        <Text font="bold16" text={t('application.removeconfirm.warning')} />
      </Stack>
      <Modal.Actions>
        <Button
          variant="secondary"
          onClick={closeModal}
          isDisabled={submit.isLoading}
          text={t('application.removeconfirm.button.cancel')}
        />
        <Button
          variant="primary-red"
          onClick={() => submit.mutate()}
          isDisabled={submit.isLoading}
          text={t('application.removeconfirm.button.submit')}
        />
      </Modal.Actions>
    </Modal>
  );
};
